import React, { useEffect, useState } from 'react';
import { Button, useNotify } from 'react-admin';
import TextField from '@material-ui/core/TextField';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Typography from '@material-ui/core/Typography';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import VisibilityIcon from '@material-ui/icons/Visibility';
import MergeTypeIcon from '@material-ui/icons/MergeType';
import dataProvider from './../lib/dataProvider';

const dateRange = user => {
  if (!user.first_tour && !user.last_tour) return '-';
  if (user.first_tour===user.last_tour) return user.first_tour;
  return (user.first_tour || '-')+' – '+(user.last_tour || '-');
};

const userName = user => user.full_name || ((user.vorname || '')+' '+(user.name || '')).trim() || '-';

const UserDetails = ({ detail }) => {
  if (!detail) return null;
  return (
    <div>
      {detail.tours.length ?
        <table style={{ width: '100%', fontSize: '0.9em' }}>
          <thead>
            <tr><th>Tour</th><th>Datum / Dauer</th><th>Höhle</th><th>Bemerkung</th><th>Status</th></tr>
          </thead>
          <tbody>
            {detail.tours.map(tour =>
              <tr key={tour.participant_id}>
                <td>{tour.id || '-'}</td>
                <td>{tour.start_datum || '-'}{tour.end_datum ? ' – '+tour.end_datum : ''}{tour.dauer ? ' / '+tour.dauer : ''}</td>
                <td>{tour.cave_name || '-'}</td>
                <td>{tour.bemerkung || '-'}</td>
                <td>{tour.deletetime ? 'Tour gelöscht' : 'aktiv'}</td>
              </tr>
            )}
          </tbody>
        </table>
      : <Typography>Keine Tourenteilnahmen vorhanden.</Typography>}
      {detail.owner_tours.length ? <Typography style={{ marginTop: '1em' }}>Touren als Autor: {detail.owner_tours.length}</Typography> : null}
      {detail.comments.length ? <Typography>Kommentare als Autor: {detail.comments.length}</Typography> : null}
    </div>
  );
};

const UserMerge = () => {
  const notify = useNotify();
  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState('');
  const [includeZzz, setIncludeZzz] = useState(false);
  const [eligibleOnly, setEligibleOnly] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState({});
  const [sources, setSources] = useState([]);
  const [target, setTarget] = useState('');
  const [preview, setPreview] = useState(false);
  const [loading, setLoading] = useState(false);

  const listParams = filter => ({
    pagination: { page: 1, perPage: 10000 },
    sort: { field: 'name', order: 'ASC' },
    filter: filter,
  });

  const loadUsers = () => {
    dataProvider.getList('userMerge', listParams({q:query, include_zzz:includeZzz, eligible_only:eligibleOnly}))
      .then(result => setUsers(result.data))
      .catch(error => notify(error.message || 'Benutzer konnten nicht geladen werden.', 'warning'));
  };

  useEffect(() => {
    loadUsers();
  }, [query, includeZzz, eligibleOnly]);

  const invalidatePreview = () => setPreview(false);

  const toggleDetail = id => {
    if (expanded===id) {
      setExpanded(false);
      return;
    }
    setExpanded(id);
    if (!details[id]) {
      dataProvider.getOne('userMerge/detail', {id:id})
        .then(result => setDetails({...details, [id]:result.data}))
        .catch(error => notify(error.message || 'Details konnten nicht geladen werden.', 'warning'));
    }
  };

  // ids als string vergleichen: je nach abfrage (z.b. nach dem filtern) kommen sie als zahl oder als string
  const inSources = id => sources.some(source => String(source.id)===String(id));

  const addSource = user => {
    if (inSources(user.id)) return;
    setSources([...sources, user]);
    invalidatePreview();
  };

  const removeSource = id => {
    setSources(sources.filter(source => String(source.id)!==String(id)));
    if (String(target)===String(id)) setTarget('');
    invalidatePreview();
  };

  const getPayload = () => {
    if (!target) return false;
    const sourceUsers = sources.filter(source => String(source.id)!==String(target));
    if (!sourceUsers.length || sourceUsers.some(source => !source.merge_eligible)) return false;
    const sourceIds = sourceUsers.map(source => source.id);
    return {target_user_id:target, source_user_ids:sourceIds};
  };

  const previewMerge = () => {
    const payload = getPayload();
    if (!payload) return;
    setLoading(true);
    dataProvider.create('userMerge/preview', {
      data: payload,
    })
      .then(result => {
        if (!result.data.target_user || !Array.isArray(result.data.source_users)) {
          throw new Error('Ungültige Antwort für die Merge-Vorschau.');
        }
        setPreview(result.data);
        setLoading(false);
      })
      .catch(error => {
        setLoading(false);
        notify(error.message || 'Vorschau konnte nicht erstellt werden.', 'warning');
      });
  };

  const executeMerge = () => {
    if (!preview || !window.confirm('Die Zusammenführung jetzt endgültig ausführen?')) return;
    const payload = getPayload();
    if (!payload) return;
    setLoading(true);
    dataProvider.create('userMerge/execute', {
      data: payload,
    })
      .then(result => {
        setLoading(false);
        notify(result.data.moved_count+' Teilnahmen wurden umgehängt, '+result.data.removed_count+' doppelte Teilnahmen entfernt.', 'info');
        setSources([]);
        setTarget('');
        setPreview(false);
        setDetails({});
        loadUsers();
      })
      .catch(error => {
        setLoading(false);
        notify(error.message || 'Zusammenführung konnte nicht ausgeführt werden.', 'warning');
      });
  };

  return (
    <div style={{ padding: '1em', maxWidth: 1600 }}>
      <h2>Benutzer zusammenführen</h2>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1em', flexWrap: 'wrap' }}>
        <TextField label="Benutzer suchen" value={query} onChange={event => setQuery(event.target.value)} />
        <FormControlLabel
          control={<Checkbox checked={includeZzz} onChange={event => setIncludeZzz(event.target.checked)} />}
          label="Bereits mit zzz markierte Benutzer anzeigen"
        />
        <FormControlLabel
          control={<Checkbox checked={eligibleOnly} onChange={event => setEligibleOnly(event.target.checked)} />}
          label="Nur mergefähige Quellen anzeigen"
        />
      </div>

      <div style={{ marginTop: '1em' }}>
        {users.map(user =>
          <ExpansionPanel key={user.id} expanded={expanded===user.id} onChange={() => toggleDetail(user.id)}>
            <ExpansionPanelSummary expandIcon={<ExpandMoreIcon />}>
              <div style={{ display: 'flex', width: '100%', alignItems: 'center', gap: '1em', flexWrap: 'wrap' }}>
                <strong>{userName(user)}</strong>
                <span>ID: {user.id}</span>
                <span>{user.benutzername || '-'}</span>
                <span>Touren: {user.tour_anz}</span>
                <span>{dateRange(user)}</span>
                {!user.merge_eligible ? <span style={{ color: '#a00' }}>{user.merge_blockers.join(', ')}</span> : <span style={{ color: '#070' }}>mergefähig</span>}
                <span style={{ marginLeft: 'auto' }}>
                  {/* stopPropagation: klick auf den button soll das akkordeon nicht auf-/zuklappen */}
                  <Button label="Zum Merge hinzufügen" variant="outlined" size="small"
                    onClick={event => { event.stopPropagation(); addSource(user); }}
                    onFocus={event => event.stopPropagation()}
                    disabled={inSources(user.id)}>
                    <AddIcon />
                  </Button>
                </span>
              </div>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails style={{ display: 'block' }}>
              <UserDetails detail={details[user.id]} />
            </ExpansionPanelDetails>
          </ExpansionPanel>
        )}
      </div>

      <div style={{ marginTop: '2em' }}>
        <h3>Merge-Warenkorb</h3>
        {!sources.length ? <Typography>Keine Benutzer ausgewählt.</Typography> : null}
        {sources.map(source =>
          <div key={source.id} style={{ marginBottom: '0.4em' }}>
            <label style={{ marginRight: '1em' }}>
              <input type="radio" checked={String(target)===String(source.id)} disabled={source.name.indexOf('zzz ')===0} onChange={() => { setTarget(source.id); invalidatePreview(); }} />
              {' '}Behalten
            </label>
            <strong>{userName(source)}</strong> (ID {source.id}) – Touren: {source.tour_anz} – {dateRange(source)}{source.benutzername ? ' – '+source.benutzername : ''}
            {!source.merge_eligible ? <span style={{ color: '#a00' }}> – nur als Ziel: {source.merge_blockers.join(', ')}</span> : null}
            <Button label="Entfernen" variant="outlined" size="small" style={{ marginLeft: '1em' }} onClick={() => removeSource(source.id)}><RemoveIcon /></Button>
          </div>
        )}
        <div style={{ marginTop: '1em' }}>
          <Button label="Vorschau erstellen" variant="contained" onClick={previewMerge} disabled={!getPayload() || loading}>
            <VisibilityIcon />
          </Button>
        </div>
      </div>

      {preview ?
        <div style={{ marginTop: '2em' }}>
          <h3>Vorschau</h3>
          <Typography>Zielbenutzer: <strong>{userName(preview.target_user)} (ID {preview.target_user.id})</strong></Typography>
          {preview.source_users.map(source =>
            <div key={source.id} style={{ marginTop: '1em' }}>
              <strong>{userName(source)} (ID {source.id})</strong> wird als <strong>{source.planned_name}</strong> markiert.
              <div>{source.tours.length} Teilnahmen.</div>
              {source.tours.map(tour =>
                <div key={tour.participant_id} style={{ marginLeft: '1em' }}>
                  Tour {tour.id || '-'}: {tour.target_already_participant ? 'Quellteilnahme entfernen (Ziel bereits Teilnehmer)' : 'Teilnahme umhängen'}; {tour.planned_note}
                </div>
              )}
            </div>
          )}
          <div style={{ marginTop: '1em' }}>
            <Button label="Zusammenführung ausführen" variant="contained" onClick={executeMerge} disabled={loading}>
              <MergeTypeIcon />
            </Button>
          </div>
        </div>
      : null}
    </div>
  );
};

export default {
  options: { label: 'Benutzer zusammenführen' },
  list: (props) => <UserMerge {...props} />,
};
